<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Sidebar -->
    <div class="w-64 bg-white border-l border-gray-200 p-6">
      <h2 class="text-xl font-bold text-gray-800 mb-6">المحامون</h2>
      
      <!-- Search -->
      <div class="mb-6">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="بحث في المحامين..."
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <!-- Filters -->
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">التخصص</label>
          <select
            v-model="selectedSpecialization"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">جميع التخصصات</option>
            <option v-for="spec in specializations" :key="spec.id" :value="spec.id">
              {{ spec.name }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">المدينة</label>
          <select
            v-model="selectedCity"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">جميع المدن</option>
            <option v-for="city in cities" :key="city.id" :value="city.id">
              {{ city.name }}
            </option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">التقييم</label>
          <select
            v-model="selectedRating"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option :value="null">جميع التقييمات</option>
            <option :value="5">5 نجوم</option>
            <option :value="4">4 نجوم فأكثر</option>
            <option :value="3">3 نجوم فأكثر</option>
            <option :value="2">2 نجوم فأكثر</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-6">
      <div class="max-w-6xl mx-auto">
        <!-- Lawyers Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="lawyer in filteredLawyers"
            :key="lawyer.id"
            class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            @click="openLawyerModal(lawyer)"
          >
            <div class="flex items-start space-x-4 space-x-reverse mb-4">
              <img
                :src="lawyer.avatar"
                :alt="lawyer.name"
                class="w-16 h-16 rounded-full object-cover"
              />
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-gray-800 mb-1">{{ lawyer.name }}</h3>
                <p class="text-sm text-gray-600">{{ lawyer.specialization }}</p>
              </div>
            </div>
            
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center">
                <div class="flex text-yellow-400">
                  <Star
                    v-for="i in lawyer.rating"
                    :key="i"
                    :class="i < lawyer.rating ? 'w-4 h-4 fill-current' : 'w-4 h-4'"
                  />
                </div>
                <span class="text-sm text-gray-600 mr-1">({{ lawyer.rating }})</span>
              </div>
              <div
                class="w-2 h-2 rounded-full"
                :class="lawyer.isOnline ? 'bg-green-500' : 'bg-gray-300'"
              ></div>
            </div>

            <div class="flex items-center text-xs text-gray-500 mb-3">
              <MapPin class="w-3 h-3 ml-1" />
              <span>{{ lawyer.city }}</span>
              <span class="mx-2">•</span>
              <span>{{ lawyer.experience }} سنة خبرة</span>
              <span class="mx-2">•</span>
              <span>{{ lawyer.casesHandled }} قضية معالجة</span>
            </div>

            <div class="flex items-center justify-between">
              <span class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                {{ lawyer.successRate }}% نسبة نجاح
              </span>
              <button class="text-blue-600 hover:text-blue-800 text-sm font-medium">
                عرض التفاصيل
              </button>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="filteredLawyers.length === 0" class="text-center py-12">
          <User class="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-600">لم يتم العثور على محامين مطابقين للبحث</p>
        </div>
      </div>
    </div>

    <!-- Lawyer Details Modal -->
    <div
      v-if="selectedLawyer"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      @click="closeLawyerModal"
    >
      <div
        class="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <!-- Header -->
        <div class="bg-white shadow-sm border-b border-gray-200">
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h1 class="text-2xl font-bold text-gray-900">
                Lawyer Details
              </h1>
              <button
                @click="closeLawyerModal"
                class="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
              >
                <X class="w-6 h-6" />
              </button>
            </div>
            
            <div class="flex items-start space-x-6 space-x-reverse">
              <img
                :src="selectedLawyer.avatar"
                :alt="selectedLawyer.name"
                class="w-32 h-32 rounded-full object-cover border-4 border-gray-100"
              />
              <div class="flex-1">
                <h2 class="text-3xl font-bold text-gray-900 mb-2">{{ selectedLawyer.name }}</h2>
                <p class="text-xl text-gray-600 mb-4">{{ selectedLawyer.specialization }}</p>
                
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div class="flex items-center space-x-2 space-x-reverse">
                    <MapPin class="w-5 h-5 text-blue-500" />
                    <div>
                      <p class="text-sm text-gray-500">Location</p>
                      <p class="font-medium text-gray-900">{{ selectedLawyer.city }}</p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2 space-x-reverse">
                    <Calendar class="w-5 h-5 text-blue-500" />
                    <div>
                      <p class="text-sm text-gray-500">Experience</p>
                      <p class="font-medium text-gray-900">{{ selectedLawyer.experience }} years</p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2 space-x-reverse">
                    <Briefcase class="w-5 h-5 text-blue-500" />
                    <div>
                      <p class="text-sm text-gray-500">Cases</p>
                      <p class="font-medium text-gray-900">{{ selectedLawyer.casesHandled }}</p>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2 space-x-reverse">
                    <Award class="w-5 h-5 text-blue-500" />
                    <div>
                      <p class="text-sm text-gray-500">Success Rate</p>
                      <p class="font-medium text-gray-900">{{ selectedLawyer.successRate }}%</p>
                    </div>
                  </div>
                </div>

                <div class="flex items-center space-x-4">
                  <div class="flex text-yellow-400">
                    <Star
                      v-for="i in 5"
                      :key="i"
                      :class="i <= selectedLawyer.rating ? 'w-6 h-6 fill-current' : 'w-6 h-6'"
                    />
                  </div>
                  <span class="text-lg font-semibold text-gray-700">{{ selectedLawyer.rating }}/5.0</span>
                  <span class="text-sm text-gray-500">({{ selectedLawyer.reviews || 0 }} reviews)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content -->
        <div class="p-6">
          <!-- About Section -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">About the Lawyer</h3>
              <div class="bg-gray-50 rounded-lg p-4">
                <p class="text-gray-700 leading-relaxed whitespace-pre-wrap">{{ selectedLawyer.bio }}</p>
              </div>
            </div>
          </div>

          <!-- Expertise Section -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Areas of Expertise</h3>
              <div class="flex flex-wrap gap-3">
                <span
                  v-for="area in selectedLawyer.expertise"
                  :key="area"
                  class="px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium border border-blue-200"
                >
                  {{ area }}
                </span>
              </div>
            </div>
          </div>

          <!-- Languages Section -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Languages</h3>
              <div class="flex flex-wrap gap-3">
                <span
                  v-for="language in selectedLawyer.languages"
                  :key="language"
                  class="px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium border border-green-200"
                >
                  {{ language }}
                </span>
              </div>
            </div>
          </div>

          <!-- Contact Information -->
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
            <div class="p-6">
              <h3 class="text-xl font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div class="flex items-center space-x-3 space-x-reverse">
                  <Phone class="w-5 h-5 text-gray-400" />
                  <span class="text-gray-700">{{ selectedLawyer.phone || 'Not available' }}</span>
                </div>
                <div class="flex items-center space-x-3 space-x-reverse">
                  <Mail class="w-5 h-5 text-gray-400" />
                  <span class="text-gray-700">{{ selectedLawyer.email || 'Not available' }}</span>
                </div>
                <div class="flex items-center space-x-3 space-x-reverse">
                  <Globe class="w-5 h-5 text-gray-400" />
                  <span class="text-gray-700">{{ selectedLawyer.website || 'Not available' }}</span>
                </div>
                <div class="flex items-center space-x-3 space-x-reverse">
                  <MapPin class="w-5 h-5 text-gray-400" />
                  <span class="text-gray-700">{{ selectedLawyer.address || 'Not available' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="bg-gray-50 border-t border-gray-200 p-6">
          <div class="flex justify-center space-x-4 space-x-reverse">
            <button
              @click="startConsultation"
              class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 space-x-reverse font-medium"
            >
              <MessageSquare class="w-5 h-5" />
              <span>Start Consultation</span>
            </button>
            <button
              @click="bookAppointment"
              class="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 space-x-reverse font-medium"
            >
              <Calendar class="w-5 h-5" />
              <span>Book Appointment</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { User, MapPin, Briefcase, Award, Star, MessageSquare, Calendar, X, Phone, Mail, Globe } from 'lucide-vue-next'

interface Lawyer {
  id: string
  name: string
  avatar: string
  specialization: string
  city: string
  experience: number
  rating: number
  casesHandled: number
  successRate: number
  isOnline: boolean
  bio: string
  expertise: string[]
  languages: string[]
  reviews?: number
  phone?: string
  email?: string
  website?: string
  address?: string
}

const searchQuery = ref('')
const selectedSpecialization = ref('')
const selectedCity = ref('')
const selectedRating = ref<number | null>(null)
const selectedLawyer = ref<Lawyer | null>(null)

const specializations = [
  { id: 'civil', name: 'القانون المدني' },
  { id: 'criminal', name: 'القانون الجزائي' },
  { id: 'commercial', name: 'القانون التجاري' },
  { id: 'labor', name: 'قانون العمل' },
  { id: 'family', name: 'القانون الأسري' },
  { id: 'corporate', name: 'القانون الشركات' }
]

const cities = [
  { id: 'amman', name: 'عمان' },
  { id: 'irbid', name: 'إربد' },
  { id: 'zarqa', name: 'الزرقاء' },
  { id: 'aqaba', name: 'العقبة' },
  { id: 'salt', name: 'السلط' }
]

const lawyers: Lawyer[] = [
  {
    id: '1',
    name: 'أحمد خليل المحامي',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2?w=150&h=150&fit=crop&crop=face',
    specialization: 'القانون المدني والتجاري',
    city: 'عمان',
    experience: 15,
    rating: 5,
    casesHandled: 342,
    successRate: 95,
    isOnline: true,
    bio: 'محامي متخصص في القانون المدني والتجاري بخبرة 15 عاماً في التعامل مع القضايا المعقدة. حاصل على درجة الماجستير في القانون من الجامعة الأردنية وعضو في نقابة المحامين الأردنيين.',
    expertise: ['القضايا المدنية', 'القضايا التجارية', 'التحكيم', 'العقود'],
    languages: ['العربية', 'الإنجليزية']
  },
  {
    id: '2',
    name: 'سارة أحمد',
    avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b018c?w=150&h=150&fit=crop&crop=face',
    specialization: 'القانون الجزائي',
    city: 'إربد',
    experience: 12,
    rating: 4,
    casesHandled: 256,
    successRate: 88,
    isOnline: false,
    bio: 'محامية متخصصة في القانون الجزائي مع خبرة واسعة في القضايا الجنائية. مهتمة بحقوق الإنسان والدفاع عن المتهمين في مختلف القضايا.',
    expertise: ['القضايا الجنائية', 'الجرائم الإلكترونية', 'حقوق الإنسان', 'الدفاع'],
    languages: ['العربية', 'الفرنسية']
  }
]

const filteredLawyers = computed(() => {
  let filtered = lawyers

  if (selectedSpecialization.value) {
    filtered = filtered.filter(lawyer => 
      lawyer.specialization.toLowerCase().includes(selectedSpecialization.value.toLowerCase())
    )
  }

  if (selectedCity.value) {
    filtered = filtered.filter(lawyer => lawyer.city === selectedCity.value)
  }

  if (selectedRating.value !== null) {
    filtered = filtered.filter(lawyer => lawyer.rating >= selectedRating.value!)
  }

  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(lawyer =>
      lawyer.name.toLowerCase().includes(query) ||
      lawyer.specialization.toLowerCase().includes(query) ||
      lawyer.city.toLowerCase().includes(query)
    )
  }

  return filtered
})

const openLawyerModal = (lawyer: Lawyer) => {
  selectedLawyer.value = lawyer
}

const closeLawyerModal = () => {
  selectedLawyer.value = null
}

const startConsultation = () => {
  console.log('Starting consultation with:', selectedLawyer.value?.name)
  // TODO: Navigate to consultation page
}

const bookAppointment = () => {
  console.log('Booking appointment with:', selectedLawyer.value?.name)
  // TODO: Navigate to appointment booking page
}

const _scheduleAppointment = () => {
  console.log('Scheduling appointment with:', selectedLawyer.value?.name)
  // TODO: Navigate to appointment scheduling
}
</script>
